import { onUnmounted, ref, watch } from 'vue';

import actionPlaceholder from '@/assets/images/action/placeholder.png';

export const ACTION_PLACEHOLDER = actionPlaceholder;

/**
 * Swap a broken <img> for its placeholder, once. Without the guard a
 * placeholder that is itself missing re-enters this handler and the browser
 * spins on the same failed request forever.
 */
export function fallbackOnError(event, placeholder = ACTION_PLACEHOLDER) {
  const el = event?.target;
  if (!el || el.dataset.imageFallbackApplied === 'true') return;
  el.dataset.imageFallbackApplied = 'true';
  el.src = placeholder;
}

/**
 * A CSS `background-image` that 404s fires no event and leaves an empty box,
 * so the URL has to be probed off-screen before it is handed to the style.
 * Returns a ref that holds the placeholder until the real image is known to
 * load, and reverts to it if the source later changes to something broken.
 *
 * The placeholder is the caller's to choose — there is no single right one.
 * An avatar falls back to the gender-specific portrait the backend would have
 * assigned (`@/utils/avatarPlaceholder`); other subjects have their own.
 */
export function useImageWithFallback(source, placeholder) {
  const resolved = ref(placeholder);
  let probe = null;

  const release = () => {
    if (!probe) return;
    probe.onload = null;
    probe.onerror = null;
    probe = null;
  };

  const stop = watch(
    source,
    url => {
      release();
      if (!url) {
        resolved.value = placeholder;
        return;
      }
      const candidate = new Image();
      probe = candidate;
      candidate.onload = () => {
        if (probe !== candidate) return;
        resolved.value = url;
        release();
      };
      candidate.onerror = () => {
        if (probe !== candidate) return;
        resolved.value = placeholder;
        release();
      };
      candidate.src = url;
    },
    { immediate: true },
  );

  onUnmounted(() => {
    stop();
    release();
  });

  return resolved;
}
